import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { apiClient } from '@/services/apiClient';
import type { Season } from '@/types';

interface SeasonState {
  // State
  seasonId: string | null;
  seasonLabel: string | null;
  seasons: Season[];
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  setSeason: (season: Season) => void;
  loadSeasons: () => Promise<void>;
  initializeDefaultSeason: () => Promise<void>;
  clearSeason: () => void;
  setLoading: (loading: boolean) => void;

  // Getters
  getCurrentSeason: () => Season | null;
  getSeasonById: (id: string) => Season | null;
}

export const useSeasonStore = create<SeasonState>()(
  persist(
    (set, get) => ({
      // Initial state
      seasonId: null,
      seasonLabel: null,
      seasons: [],
      isLoading: false,
      isInitialized: false,

      // Actions
      setSeason: (season: Season): void => {
        set({
          seasonId: season.id,
          seasonLabel: season.label,
        });

        // Persist to localStorage
        localStorage.setItem('arma4you.season', JSON.stringify({
          id: season.id,
          label: season.label,
        }));

        // Invalidate all queries that depend on season
        queryClient.invalidateQueries();

        toast.success(`Stagione cambiata: ${season.label}`);
      },

      loadSeasons: async (): Promise<void> => {
        set({ isLoading: true });

        try {
          const response = await apiClient.getSeasons();

          if (response.error) {
            console.error('Failed to load seasons:', response.error);
            toast.error('Errore nel caricamento delle stagioni');
            return;
          }

          if (response.data) {
            set({ seasons: response.data });
          }
        } catch (error) {
          console.error('Error loading seasons:', error);
          toast.error('Errore nel caricamento delle stagioni');
        } finally {
          set({ isLoading: false });
        }
      },

      initializeDefaultSeason: async (): Promise<void> => {
        const { seasonId, seasons, loadSeasons } = get();

        // If we already have a season set, don't change it
        if (seasonId && seasons.some(s => s.id === seasonId)) {
          set({ isInitialized: true });
          return;
        }

        // Load seasons if not already loaded
        if (seasons.length === 0) {
          await loadSeasons();
        }

        const updatedSeasons = get().seasons;

        if (updatedSeasons.length === 0) {
          set({ isInitialized: true });
          return;
        }

        // Try to find current season or use the first available
        let defaultSeason = updatedSeasons.find(s => s.is_current);
        
        if (!defaultSeason) {
          // If no current season, try to get from API
          try {
            const response = await apiClient.getCurrentSeason();
            if (response.data) {
              defaultSeason = response.data;
            }
          } catch (error) {
            console.warn('Could not get current season from API:', error);
          }
        }

        // Fall back to first season if no current season found
        if (!defaultSeason) {
          defaultSeason = updatedSeasons[0];
        }

        if (defaultSeason) {
          set({
            seasonId: defaultSeason.id,
            seasonLabel: defaultSeason.label,
          });

          localStorage.setItem('arma4you.season', JSON.stringify({
            id: defaultSeason.id,
            label: defaultSeason.label,
          }));
        }

        set({ isInitialized: true });
      },

      clearSeason: (): void => {
        set({
          seasonId: null,
          seasonLabel: null,
        });

        localStorage.removeItem('arma4you.season');
        
        // Invalidate queries
        queryClient.invalidateQueries();
      },

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },

      // Getters
      getCurrentSeason: (): Season | null => {
        const { seasonId, seasons } = get();
        if (!seasonId) return null;
        return seasons.find(s => s.id === seasonId) || null;
      },

      getSeasonById: (id: string): Season | null => {
        const { seasons } = get();
        return seasons.find(s => s.id === id) || null;
      },
    }),
    {
      name: 'arma4you-season',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        seasonId: state.seasonId,
        seasonLabel: state.seasonLabel,
        seasons: state.seasons,
      }),
      onRehydrateStorage: () => (state) => {
        // Validate stored season data on app load
        if (state?.seasonId && state?.seasonLabel) {
          // Season data exists, restore it
          localStorage.setItem('arma4you.season', JSON.stringify({
            id: state.seasonId,
            label: state.seasonLabel,
          }));
        }
      },
    }
  )
);

// Utility hooks
export const useSeason = () => {
  const store = useSeasonStore();
  return {
    ...store,
    currentSeason: store.getCurrentSeason(),
  };
};

export const useCurrentSeason = () => {
  return useSeasonStore(state => ({
    seasonId: state.seasonId,
    seasonLabel: state.seasonLabel,
    currentSeason: state.getCurrentSeason(),
  }));
};

export const useSeasons = () => {
  return useSeasonStore(state => state.seasons);
};

// Initialize season store on app startup
export const initializeSeasonStore = async () => {
  const store = useSeasonStore.getState();
  if (!store.isInitialized) {
    await store.initializeDefaultSeason();
  }
};